import type { ThreadMessage } from '@assistant-ui/react';
import { z } from 'zod';

/** Thread metadata persisted to localStorage. */
export type StoredThread = {
  readonly status: 'regular' | 'archived';
  readonly remoteId: string;
  readonly externalId?: string | undefined;
  readonly title?: string | undefined;
};

/** A single message item stored in thread history. */
export type StoredMessageItem = {
  message: ThreadMessage;
  parentId: string | null;
};

/** Exported message repository shape matching assistant-ui's ExportedMessageRepository. */
export type StoredMessageRepository = {
  headId: string | null;
  messages: StoredMessageItem[];
};

/** Thread list response matching assistant-ui's RemoteThreadListResponse. */
export type StoredThreadListResponse = {
  threads: StoredThread[];
};

/** Thread initialize response. */
export type StoredThreadInitializeResponse = {
  remoteId: string;
  externalId: string;
};

const StoredThreadSchema = z.object({
  status: z.enum(['regular', 'archived']),
  remoteId: z.string(),
  externalId: z.string().optional(),
  title: z.string().optional(),
});

const ThreadsRecordSchema = z.record(z.string(), StoredThreadSchema);

const StoredMessageItemSchema = z.object({
  message: z.looseObject({ id: z.string() }),
  parentId: z.string().nullable(),
});

const StoredMessageRepositorySchema = z.object({
  headId: z.string().nullable(),
  messages: z.array(StoredMessageItemSchema),
});

export function createThreadListStore(storage: Storage) {
  const StorageKey = 'atlas-threads';

  function readAll(): Record<string, StoredThread> {
    const raw = storage.getItem(StorageKey);
    if (!raw) {
      return {};
    }
    const result = ThreadsRecordSchema.safeParse(JSON.parse(raw));
    if (!result.success) {
      return {};
    }
    return result.data as Record<string, StoredThread>;
  }

  function writeAll(threads: Record<string, StoredThread>): void {
    storage.setItem(StorageKey, JSON.stringify(threads));
  }

  function getOrThrow(remoteId: string): { thread: StoredThread; threads: Record<string, StoredThread> } {
    const threads = readAll();
    const thread = threads[remoteId];
    if (!thread) {
      throw new Error(`Thread not found: ${remoteId}`);
    }
    return { thread, threads };
  }

  return {
    list(): StoredThreadListResponse {
      const threads = readAll();
      return { threads: Object.values(threads) };
    },

    initialize(localId: string): StoredThreadInitializeResponse {
      const threads = readAll();
      threads[localId] = { status: 'regular', remoteId: localId, externalId: localId };
      writeAll(threads);
      return { remoteId: localId, externalId: localId };
    },

    rename(remoteId: string, title: string): void {
      const { thread, threads } = getOrThrow(remoteId);
      threads[remoteId] = { ...thread, title };
      writeAll(threads);
    },

    archive(remoteId: string): void {
      const { thread, threads } = getOrThrow(remoteId);
      threads[remoteId] = { ...thread, status: 'archived' };
      writeAll(threads);
    },

    unarchive(remoteId: string): void {
      const { thread, threads } = getOrThrow(remoteId);
      threads[remoteId] = { ...thread, status: 'regular' };
      writeAll(threads);
    },

    delete(remoteId: string): void {
      const { threads } = getOrThrow(remoteId);
      delete threads[remoteId];
      writeAll(threads);
      storage.removeItem(`atlas-thread-messages:${remoteId}`);
    },

    fetch(remoteId: string): StoredThread {
      return getOrThrow(remoteId).thread;
    },
  };
}

export function createThreadHistoryStore(storage: Storage, threadId: string) {
  const StorageKey = `atlas-thread-messages:${threadId}`;

  function readRepo(): StoredMessageRepository {
    const raw = storage.getItem(StorageKey);
    if (!raw) {
      return { headId: null, messages: [] };
    }
    const result = StoredMessageRepositorySchema.safeParse(JSON.parse(raw));
    if (!result.success) {
      return { headId: null, messages: [] };
    }
    return result.data as StoredMessageRepository;
  }

  function writeRepo(repo: StoredMessageRepository): void {
    storage.setItem(StorageKey, JSON.stringify(repo));
  }

  return {
    load(): StoredMessageRepository {
      return readRepo();
    },

    append(item: StoredMessageItem): void {
      const repo = readRepo();
      writeRepo({
        headId: item.message.id,
        messages: [...repo.messages, item],
      });
    },

    save(repo: StoredMessageRepository): void {
      writeRepo(repo);
    },
  };
}
